/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useMemo } from "react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "react-toastify";
import { RefreshCw, Trophy, TrendingUp, Star, Filter } from "lucide-react";
import {
  useGlobalLeaderboard,
  useLeaderboardStats,
  useTopPlayers,
  useTopCaptains,
  useInvalidateCache,
  useLeaderboardByGameweek,
} from "@/hooks/api/useFantasyLeaderboard";
import { useGameweeks } from "@/hooks/api/useGameweeks";
import LeaderboardTable from "@/pages/FantasyLeaderboard/FantasyLeaderboardTable";
import LeaderboardStatsCard from "@/pages/FantasyLeaderboard/FantasyLeaderboardStatsCard";
import TopPlayersCard from "@/pages/FantasyLeaderboard/TopPlayersCard";
import TopCaptainsCard from "@/pages/FantasyLeaderboard/TopCaptainsCard";
import type { Gameweek } from "@/types/api.types";

type Tab = "global" | "players" | "captains";

export default function FantasyLeaderboard() {
  const [activeTab, setActiveTab] = useState<Tab>("global");
  const [selectedGameweekId, setSelectedGameweekId] = useState<number | null>(
    null,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const offset = (currentPage - 1) * pageSize;
  const { data: globalData, isLoading: globalLoading } =
    useGlobalLeaderboard(pageSize, offset);
  const { data: gwData, isLoading: gwLoading } =
    useLeaderboardByGameweek(selectedGameweekId);
  const { data: stats } = useLeaderboardStats();
  const { data: topPlayersData } = useTopPlayers(10);
  const { data: topCaptainsData } = useTopCaptains(10);
  const { mutate: invalidateCache, isPending: isInvalidating } =
    useInvalidateCache();
  const { data: gameweeks } = useGameweeks();

  const handleInvalidateCache = () => {
    invalidateCache(undefined, {
      onSuccess: () => toast.success("Cache cleared \u2014 fresh data loaded!"),
      onError: () => toast.error("Failed to clear cache"),
    });
  };

  // Non-UPCOMING gameweeks for the filter
  const sortedGameweeks = useMemo(
    () =>
      [...(gameweeks ?? [])].filter(
        (gw: Gameweek) => gw.status !== "UPCOMING",
      ),
    [gameweeks],
  );

  const selectedGw = sortedGameweeks.find(
    (gw: Gameweek) => gw.id === selectedGameweekId,
  );

  // Which leaderboard data to show
  const leaderboardEntries = selectedGameweekId
    ? (gwData?.leaderboard ?? []).map((e: any) => ({
        ...e,
        // For gameweek view, show gameweekPoints as the main points column
        totalPoints: e.gameweekPoints ?? e.totalPoints,
      }))
    : (globalData?.leaderboard ?? []);
  const isLeaderboardLoading = selectedGameweekId ? gwLoading : globalLoading;
  const hasCompletedGameweeks = (globalData as any)?.hasCompletedGameweeks ?? true;

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    {
      key: "global",
      label: "Global Rankings",
      icon: <Trophy className="h-4 w-4" />,
    },
    {
      key: "players",
      label: "Top Players",
      icon: <TrendingUp className="h-4 w-4" />,
    },
    {
      key: "captains",
      label: "Top Captains",
      icon: <Star className="h-4 w-4" />,
    },
  ];

  return (
    <Layout>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Fantasy Leaderboard
          </h1>
          <p className="text-gray-500 mt-1">
            Live rankings across all fantasy teams
            {stats?.fromCache && (
              <Badge className="ml-2 bg-yellow-100 text-yellow-700 text-xs">
                Cached
              </Badge>
            )}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleInvalidateCache}
          disabled={isInvalidating}
          className="text-gray-600"
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${isInvalidating ? "animate-spin" : ""}`}
          />
          {isInvalidating ? "Refreshing..." : "Refresh Cache"}
        </Button>
      </div>

      {/* Stats Row */}
      {stats && <LeaderboardStatsCard stats={stats} />}

      {/* Tabs */}
      <div className="flex items-center justify-between mt-6 mb-4">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition ${
                activeTab === tab.key
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Gameweek Filter — only on Global Rankings tab */}
      {activeTab === "global" && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Filter className="h-3.5 w-3.5 text-gray-400" />
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Filter by Matchweek
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {sortedGameweeks.map((gw: Gameweek) => (
              <button
                key={gw.id}
                onClick={() => {
                  setSelectedGameweekId(gw.id);
                  setCurrentPage(1);
                }}
                className={`px-3 py-1.5 text-xs rounded-full font-medium transition ${
                  selectedGameweekId === gw.id
                    ? "bg-blue-600 text-white"
                    : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"
                }`}
              >
                {gw.name}
              </button>
            ))}
            <button
              onClick={() => {
                setSelectedGameweekId(null);
                setCurrentPage(1);
              }}
              className={`px-3 py-1.5 text-xs rounded-full font-medium transition ${
                !selectedGameweekId
                  ? "bg-blue-600 text-white"
                  : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"
              }`}
            >
              All Time
            </button>
          </div>
        </div>
      )}

      {/* Active gameweek badge */}
      {selectedGameweekId && selectedGw && activeTab === "global" && (
        <div className="mb-4 flex items-center gap-2">
          <Badge className="bg-blue-100 text-blue-800 px-3 py-1">
            Showing: {selectedGw.name}
          </Badge>
          <span className="text-sm text-gray-500">
            Rankings based on points earned in this matchweek only
          </span>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === "global" && (
        <>
          {!isLeaderboardLoading && !hasCompletedGameweeks && !selectedGameweekId && leaderboardEntries.length > 0 && (
            <div className="flex items-center gap-2 px-4 py-3 mb-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
              <span>⏳</span>
              <span>Rankings will update after the first matchday is completed. All teams currently at 0 points.</span>
            </div>
          )}
          <LeaderboardTable
            entries={leaderboardEntries}
            isLoading={isLeaderboardLoading}
            currentPage={currentPage}
            pageSize={pageSize}
            totalEntries={
              selectedGameweekId
                ? (gwData?.leaderboard?.length ?? 0)
                : (stats?.totalTeams ?? 0)
            }
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            hideRank={!hasCompletedGameweeks && !selectedGameweekId}
          />
        </>
      )}

      {activeTab === "players" && (
        <TopPlayersCard players={topPlayersData?.players ?? []} />
      )}

      {activeTab === "captains" && (
        <TopCaptainsCard captains={topCaptainsData?.captains ?? []} />
      )}
    </Layout>
  );
}
